import { OVERLAY_OPEN, OVERLAY_CLOSE } from './arctic-header-events.js';

const SEARCH_DEBOUNCE_MS = 300;
const FOCUSABLE_SELECTOR = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

class ArcticHeaderSearch extends HTMLElement {
  constructor() {
    super();
    this._activeFetch = null;
    this._searchDebounceId = null;
    this._generation = 0;
    this._selectionIndex = -1;
    this._inertSnapshots = new Map();
  }

  connectedCallback() {
    this.form = this.querySelector('[data-header-search-form]');
    this.input = this.querySelector('[data-header-search-input]');
    this.closeButton = this.querySelector('[data-header-search-close]');
    this.panel = this.querySelector('[data-header-search-panel]');
    this.results = this.querySelector('[data-header-search-results]');
    this.status = this.querySelector('[data-header-search-status]');
    this.emptyState = this.querySelector('[data-header-search-empty]');
    if (!this.form || !this.input || !this.panel || !this.results) return;

    this._emptyStateHTML = this.emptyState?.outerHTML ?? '';
    this._mobileQuery = window.matchMedia('(max-width: 749px)');
    this._setCloseButtonVisible(false);
    if (this.dataset.predictiveEnabled !== 'true') return;

    this._onFocus = () => { if (!this._restoringFocus) this.open(); };
    this._onInputClick = () => this.open();
    this._onInput = this._handleInput.bind(this);
    this._onCloseClick = (event) => { event.preventDefault(); this.close({ restoreFocus: true }); };
    this._onDocumentPointerDown = (event) => {
      if (this._isOpen && !this.contains(event.target)) this.close();
    };
    this._onDocumentKeydown = this._handleDocumentKeydown.bind(this);
    this._onFocusOut = () => {
      clearTimeout(this._focusOutTimer);
      this._focusOutTimer = setTimeout(() => {
        if (this._isOpen && !this._mobileQuery.matches && !this.contains(document.activeElement)) this.close();
      }, 0);
    };
    this._onDocumentFocus = (event) => {
      if (this._isOpen && this._mobileQuery.matches && !this.contains(event.target)) {
        this.input.focus({ preventScroll: true });
      }
    };
    this._onBreakpointChange = () => this.close();
    this._onOtherOverlay = (event) => {
      if (this._isOpen && event.detail?.el !== this.panel) this.close();
    };

    this.input.addEventListener('focus', this._onFocus);
    this.input.addEventListener('click', this._onInputClick);
    this.input.addEventListener('input', this._onInput);
    this.closeButton?.addEventListener('click', this._onCloseClick);
    this.addEventListener('focusout', this._onFocusOut);
    document.addEventListener('pointerdown', this._onDocumentPointerDown, true);
    document.addEventListener('keydown', this._onDocumentKeydown);
    document.addEventListener('focusin', this._onDocumentFocus);
    document.addEventListener(OVERLAY_OPEN, this._onOtherOverlay);
    this._mobileQuery.addEventListener('change', this._onBreakpointChange);
  }

  disconnectedCallback() {
    this.close();
    this._invalidateRequest();
    this._restoreCoveredContent();
    clearTimeout(this._focusOutTimer);
    this.input?.removeEventListener('focus', this._onFocus);
    this.input?.removeEventListener('click', this._onInputClick);
    this.input?.removeEventListener('input', this._onInput);
    this.closeButton?.removeEventListener('click', this._onCloseClick);
    this.removeEventListener('focusout', this._onFocusOut);
    document.removeEventListener('pointerdown', this._onDocumentPointerDown, true);
    document.removeEventListener('keydown', this._onDocumentKeydown);
    document.removeEventListener('focusin', this._onDocumentFocus);
    document.removeEventListener(OVERLAY_OPEN, this._onOtherOverlay);
    this._mobileQuery?.removeEventListener('change', this._onBreakpointChange);
  }

  get _isOpen() { return this.classList.contains('is-open'); }
  get query() { return this.input.value.trim(); }

  open({ fetchResults = true } = {}) {
    if (!this.panel || this.dataset.predictiveEnabled !== 'true' || this._isOpen) return;
    document.querySelectorAll('arctic-header-search.is-open').forEach((search) => {
      if (search !== this) search.close?.();
    });
    this.classList.add('is-open');
    this.panel.hidden = false;
    this._setCloseButtonVisible(true);
    document.body.classList.add('search-open');
    this._dispatchOverlayEvent(OVERLAY_OPEN);
    if (this._mobileQuery.matches) this._hideCoveredContent();
    if (!fetchResults) return;
    this._invalidateRequest();
    if (this.query) {
      this._showFallback(false);
      this._fetchPredictiveSearch(this.query, this._generation);
    } else {
      this._showEmptyState();
    }
  }

  close({ restoreFocus = false } = {}) {
    if (!this._isOpen) return;
    this.classList.remove('is-open', 'has-results');
    this.panel.hidden = true;
    this._setCloseButtonVisible(false);
    this._invalidateRequest();
    this._clearSelection();
    this.results.replaceChildren();
    this._setListbox(null);
    this._announce('');
    this._restoreCoveredContent();
    document.body.classList.toggle('search-open', !!document.querySelector('arctic-header-search.is-open'));
    this._dispatchOverlayEvent(OVERLAY_CLOSE);
    if (restoreFocus && this.input.getClientRects().length) {
      this._restoringFocus = true;
      this.input.focus({ preventScroll: true });
      this._restoringFocus = false;
    }
  }

  _handleInput() {
    this._invalidateRequest();
    this._clearSelection();
    document.querySelectorAll('arctic-header-search [data-header-search-input]').forEach((input) => {
      if (input !== this.input) input.value = this.input.value;
    });
    this.open({ fetchResults: false });
    if (!this.query) {
      this._showEmptyState();
      return;
    }
    this._showFallback(false);
    const term = this.query;
    const generation = this._generation;
    this._searchDebounceId = setTimeout(() => {
      this._searchDebounceId = null;
      this._fetchPredictiveSearch(term, generation);
    }, SEARCH_DEBOUNCE_MS);
  }

  _handleDocumentKeydown(event) {
    if (event.isComposing) return;
    if (event.target === this.input && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      if (!this._isOpen) this.open();
      const options = this._options || [];
      if (!options.length) return;
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const next = this._selectionIndex < 0 ? (direction > 0 ? 0 : options.length - 1)
        : (this._selectionIndex + direction + options.length) % options.length;
      this._selectOption(next);
      return;
    }
    if (!this._isOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close({ restoreFocus: true });
    } else if (event.key === 'Enter' && event.target === this.input && this._selectionIndex >= 0) {
      event.preventDefault();
      this._options[this._selectionIndex]?.click();
    } else if (event.key === 'Tab' && this._mobileQuery.matches) {
      const focusable = [...this.querySelectorAll(FOCUSABLE_SELECTOR)].filter((element) =>
        element.tabIndex >= 0 && element.getClientRects().length && !element.closest('[hidden], [inert], [aria-hidden="true"]')
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) return;
      if (event.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && (document.activeElement === last || !this.contains(document.activeElement))) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }
  }

  _selectOption(index) {
    this._clearSelection();
    const option = this._options?.[index];
    if (!option) return;
    this._selectionIndex = index;
    option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  _clearSelection() {
    this._options?.forEach((option) => option.setAttribute('aria-selected', 'false'));
    this._selectionIndex = -1;
    this.input?.removeAttribute('aria-activedescendant');
  }

  _setListbox(listbox) {
    this._clearSelection();
    this._options = [];
    if (!listbox) {
      this.input.removeAttribute('aria-controls');
      this.input.setAttribute('aria-expanded', 'false');
      return;
    }
    listbox.id = `${this.input.id}-listbox`;
    this._options = [...listbox.querySelectorAll('[data-predictive-option]')];
    this._options.forEach((option, index) => { option.id = `${this.input.id}-option-${index}`; });
    this.input.setAttribute('aria-controls', listbox.id);
    this.input.setAttribute('aria-expanded', 'true');
  }

  _showEmptyState() {
    this.results.innerHTML = this._emptyStateHTML;
    this.results.removeAttribute('aria-busy');
    this.classList.remove('has-results');
    this._setListbox(null);
    this._announce('');
    this._notifySurfaces('header-search-empty');
  }

  _showFallback(failed) {
    const message = document.createElement('p');
    message.className = 'header-search__empty-copy';
    message.textContent = failed ? this.dataset.errorText : this.dataset.loadingText;
    const link = document.createElement('a');
    link.className = 'predictive-search-results__view-all';
    link.href = this._fullSearchURL();
    link.textContent = this.dataset.searchForText.replace('__QUERY__', this.query);
    this.results.replaceChildren(message, link);
    this._setListbox(null);
    this.results.setAttribute('aria-busy', failed ? 'false' : 'true');
    this.classList.remove('has-results');
    this._announce(message.textContent);
  }

  async _fetchPredictiveSearch(term, generation = this._generation) {
    if (!this._isCurrent(term, generation)) return;
    const controller = new AbortController();
    this._activeFetch = controller;
    this.classList.add('is-loading');
    const url = new URL(window.routes?.predictive_search_url || '/search/suggest', window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'product,collection,page,query');
    try {
      const response = await fetch(url.toString(), { signal: controller.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const text = await response.text();
      if (controller.signal.aborted || !this._isCurrent(term, generation)) return;
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      const sectionRoot = parsed.querySelector('#shopify-section-predictive-search');
      if (!sectionRoot) throw new Error('Predictive search response missing section');
      this.results.innerHTML = sectionRoot.innerHTML;
      const fullSearchLink = this.results.querySelector('.predictive-search-results__view-all');
      if (fullSearchLink) fullSearchLink.href = this._fullSearchURL();
      this._setListbox(this.results.querySelector('[data-predictive-search-listbox]'));
      this._announce(this.results.querySelector('[data-predictive-search-status]')?.textContent.trim() || '');
      this.classList.add('has-results');
      this._notifySurfaces('predictive-search');
    } catch (error) {
      if (error.name !== 'AbortError' && this._isCurrent(term, generation)) this._showFallback(true);
    } finally {
      if (this._activeFetch === controller) {
        this._activeFetch = null;
        this.classList.remove('is-loading');
        this.results.removeAttribute('aria-busy');
      }
    }
  }

  _fullSearchURL() {
    const url = new URL(this.form.action, window.location.origin);
    url.searchParams.set('q', this.query);
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('type', 'product,page');
    return url.toString();
  }

  _isCurrent(term, generation) {
    return this._isOpen && generation === this._generation && term === this.query;
  }

  _invalidateRequest() {
    this._generation += 1;
    clearTimeout(this._searchDebounceId);
    this._searchDebounceId = null;
    this._activeFetch?.abort();
    this._activeFetch = null;
    this.classList.remove('is-loading');
    this.results?.removeAttribute('aria-busy');
  }

  _hideCoveredContent() {
    this._restoreCoveredContent();
    for (let branch = this; branch.parentElement && branch !== document.body; branch = branch.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (sibling === branch || !(sibling instanceof HTMLElement)) continue;
        this._inertSnapshots.set(sibling, sibling.inert);
        sibling.inert = true;
      }
    }
  }

  _restoreCoveredContent() {
    this._inertSnapshots.forEach((wasInert, element) => { element.inert = wasInert; });
    this._inertSnapshots.clear();
  }

  _announce(text) { if (this.status) this.status.textContent = text; }
  _notifySurfaces(source) {
    window.dispatchEvent(new CustomEvent('northbound:surfaces-changed', { detail: { source } }));
  }

  _setCloseButtonVisible(isVisible) {
    if (!this.closeButton) return;
    this.closeButton.hidden = false;
    this.closeButton.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    this.closeButton.tabIndex = isVisible ? 0 : -1;
  }

  _dispatchOverlayEvent(name) {
    const event = new CustomEvent(name, { bubbles: true, detail: { el: this.panel, source: 'header-search' } });
    // A detached section cannot bubble to the shell that owns its scroll lock.
    (this.isConnected ? this : document).dispatchEvent(event);
  }
}

if (!customElements.get('arctic-header-search')) customElements.define('arctic-header-search', ArcticHeaderSearch);
